import dayjs from 'dayjs'

import type { CivilDateRange } from '@sportninja/common/features/game/gameListView/types'
import { paddedRequestWindow } from '@sportninja/common/features/game/gameListView/gameDayBucket'

export interface GameListFilterInput {
  sort: 'asc' | 'desc'
  teamId?: string | null
  venueId?: string | null
  facilityId?: string | null
  /** The civil window the user is actually looking at, or null for "no dates". */
  civilRange: CivilDateRange | null
  /** True when the user picked an explicit range rather than a time period. */
  hasCustomDateRange: boolean
  /** Mirrors the legacy `default=1` behaviour for an unfiltered, unsorted view. */
  hasExplicitSort: boolean
  /**
   * True when a null `civilRange` means "every date", not "no window chosen".
   *
   * The legacy `default=1` narrows an ENDED competition to games from today
   * on — that is, to nothing at all — which is the exact opposite of what the
   * Details grid asks for when it drops the Past / Today / Upcoming tabs.
   */
  wantsAllDates?: boolean
  perPage?: number
}

export type GameListQuery = Record<string, string | number>

/**
 * The three query shapes this feature needs, built from one input.
 *
 * They exist separately because they answer different questions and a single
 * shared object would silently give the wrong answer to two of them:
 *
 * - `display` is PADDED. Venue-local days do not line up with UTC days, so the
 *   request has to reach a day either side and the client filters back down.
 * - `export` is the user's ORIGINAL, unpadded semantics. CSV and Print Sign-in
 *   Sheets must contain what the filters say, not the padding the calendar
 *   needed to render its edges.
 * - `fingerprint` is the cache identity. It carries the padded fields AND the
 *   unpadded civil bounds, because padding and civil filtering are separate
 *   contracts — two different civil ranges can pad to the same request window,
 *   and sharing a cache entry between them would show the wrong days.
 */
export interface GameListQueries {
  display: GameListQuery
  export: GameListQuery
  fingerprint: Record<string, string | number | null>
}

const baseQuery = (input: GameListFilterInput): GameListQuery => {
  const query: GameListQuery = {
    order: input.sort,
    exclude_cancelled_games: 1,
  }

  if (input.teamId) {
    query.team_id = input.teamId
  }
  if (input.venueId) {
    query.venue_id = input.venueId
  }
  if (input.facilityId) {
    query.facility_id = input.facilityId
  }
  if (input.perPage) {
    query.per_page = input.perPage
  }

  return query
}

/**
 * The user's own bounds, unpadded, in the BROWSER's zone — exactly what the
 * pre-migration request sent (`dayjs(x).hour(0).minute(0).second(0).format()`).
 *
 * Not UTC midnight: `…T00:00:00Z` is a different instant from local midnight
 * everywhere except UTC, so a CSV or sign-in sheet would start and end on the
 * wrong side of the boundary for most of the world.
 */
const unpaddedWindow = (range: CivilDateRange) => ({
  starts_after:
    range.start === null
      ? undefined
      : dayjs(range.start).hour(0).minute(0).second(0).millisecond(0).format(),
  starts_before:
    range.endExclusive === null
      ? undefined
      : dayjs(range.endExclusive)
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0)
          .format(),
})

export const buildGameListQueries = (
  input: GameListFilterInput
): GameListQueries => {
  const base = baseQuery(input)
  /*
   * Officials ride on the DISPLAY request only. The Details grid renders them in
   * three columns; the export query feeds the legacy CSV and Print Sign-in
   * Sheets, which have never carried officials and gain nothing but bytes.
   *
   * Sent unconditionally rather than per view: it is a few small objects per
   * game against a payload that already carries team, venue and roster trees,
   * and making it conditional would split List, Calendar and Details across
   * separate cache entries and refetch the whole grid on every view switch.
   */
  const display: GameListQuery = { ...base, include_officials: 1 }
  const exportQuery: GameListQuery = { ...base }

  if (input.civilRange !== null) {
    //  Each bound is applied on its own: a one-sided range is a real filter the
    //  picker can produce, and dropping it would widen the query to everything.
    const padded = paddedRequestWindow(input.civilRange)
    if (padded.startsAfter !== '') {
      display.starts_after = padded.startsAfter
    }
    if (padded.startsBefore !== '') {
      display.starts_before = padded.startsBefore
    }

    //  The legacy export excluded time-period dates and only carried an
    //  explicitly-chosen range. Preserved exactly.
    if (input.hasCustomDateRange) {
      const unpadded = unpaddedWindow(input.civilRange)
      if (unpadded.starts_after !== undefined) {
        exportQuery.starts_after = unpadded.starts_after
      }
      if (unpadded.starts_before !== undefined) {
        exportQuery.starts_before = unpadded.starts_before
      }
    }
  } else if (!input.hasExplicitSort && !input.wantsAllDates) {
    display.default = 1
    exportQuery.default = 1
  }

  const fingerprint: Record<string, string | number | null> = {
    order: String(display.order),
    exclude_cancelled_games: 1,
    //  Constant today, but it describes what the CACHED PAYLOAD contains. If it
    //  ever becomes conditional, two different response shapes would otherwise
    //  share one cache entry.
    include_officials: 1,
    team_id: input.teamId || null,
    venue_id: input.venueId || null,
    facility_id: input.facilityId || null,
    per_page: input.perPage ?? null,
    starts_after: (display.starts_after as string) ?? null,
    starts_before: (display.starts_before as string) ?? null,
    //  The unpadded civil bounds are part of the identity, not a derived value.
    civil_start: input.civilRange?.start ?? null,
    civil_end_exclusive: input.civilRange?.endExclusive ?? null,
    default: (display.default as number) ?? null,
  }

  return { display, export: exportQuery, fingerprint }
}
